import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Input,
  Button,
  Divider,
  Image,
  Form,
  addToast,
} from "@heroui/react";
import { useGoogleLogin } from "@react-oauth/google";
import { getUserInfoAPI, loginAPI, loginThirdPartyAPI } from "@/api/auth";
import { STORAGE_KEYS } from "@/lib/constants";
import Logo from "@/assets/partners-logo.svg";
import { useUserStore } from "@/store/user";
import { Eye, EyeOff, Mail, Lock, X } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "이메일을 입력해주세요.")
    .email("올바른 이메일 형식을 입력해주세요."),
  password: z
    .string()
    .min(1, "비밀번호를 입력해주세요.")
    .min(6, "비밀번호는 최소 6자 이상이어야 합니다."),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const Route = createFileRoute("/")({
  component: App,
});

function App() {
  const { user, setUser, refreshUser } = useUserStore();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const navigate = useNavigate();

  const handleClickGoogle = useGoogleLogin({
    onSuccess: (res) => handleGoogleLogin(res.code),
    flow: "auth-code",
  });

  const handleGoogleLogin = async (code: string) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const { data } = await loginThirdPartyAPI({
        token: code,
        provider: "GOOGLE",
        redirectUri: null,
      });
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
      setUser(data?.userInfo);
      navigate({ to: "/dashboard" });
    } catch (error: any) {
      if (error.name === "HTTPError") {
        const errorJson = await error.response.clone().json();
        addToast({
          title: "로그인 실패",
          description: errorJson?.reason,
          icon: <X className="text-danger" />,
        });
        navigate({ to: "/" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (data: LoginFormData) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const { data: loginData } = await loginAPI(data);

      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, loginData.accessToken);
      setUser(loginData?.userInfo);
      navigate({ to: "/dashboard" });
      addToast({
        title: "로그인 성공",
        color: "success",
      });
    } catch (error: any) {
      if (error.name === "HTTPError") {
        const errorJson = await error.response.clone().json();
        addToast({
          title: "로그인 실패",
          description: errorJson?.reason,
          icon: <X className="text-danger" />,
        });
        navigate({ to: "/" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKakaoLogin = () => {
    console.log("카카오톡 로그인 시도");

    window?.Kakao?.Auth?.authorize({
      redirectUri: `${import.meta.env.VITE_APP_BASE_URL}/kakao-redirect`,
    });
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  useEffect(() => {
    getUserInfoAPI().then((res) => {
      if (res.data.userInfo) {
        navigate({ to: "/dashboard" });
      }
    });
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-background via-background to-content2/10">
      <div className="flex mb-8">
        <Image
          src={Logo}
          alt="Partners Logo"
          width={240}
          height="auto"
          className="w-60"
        />
      </div>

      <Card className="w-full max-w-md bg-background/80 backdrop-blur-md shadow-2xl border-1 border-divider">
        <CardHeader className="flex flex-col gap-3 text-center px-8 pt-8 pb-4">
          <h1 className="text-2xl font-bold text-foreground">
            파트너스 로그인
          </h1>
          <p className="text-sm text-foreground-500">
            계정에 로그인하여 서비스를 이용하세요
          </p>
        </CardHeader>

        <CardBody className="px-8 pb-8">
          <Form
            className="space-y-6"
            validationBehavior="native"
            onSubmit={handleSubmit(handleEmailLogin)}
          >
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  type="email"
                  label="이메일"
                  placeholder="user@allthatai.io"
                  variant="flat"
                  startContent={
                    <Mail className="text-foreground-400" size={18} />
                  }
                  classNames={{
                    input: "text-foreground",
                    inputWrapper:
                      "bg-content1 hover:bg-content2 border-divider",
                  }}
                  isRequired
                  errorMessage={fieldState.error?.message}
                  isInvalid={!!fieldState.error}
                  onValueChange={field.onChange}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label="비밀번호"
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder="••••••••"
                  variant="flat"
                  startContent={
                    <Lock className="text-foreground-400" size={18} />
                  }
                  endContent={
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="focus:outline-none"
                    >
                      {isPasswordVisible ? (
                        <EyeOff className="text-foreground-400" size={18} />
                      ) : (
                        <Eye className="text-foreground-400" size={18} />
                      )}
                    </button>
                  }
                  classNames={{
                    input: "text-foreground",
                    inputWrapper:
                      "bg-content1 hover:bg-content2 border-divider",
                  }}
                  isRequired
                  errorMessage={fieldState.error?.message}
                  isInvalid={!!fieldState.error}
                  onValueChange={field.onChange}
                />
              )}
            />

            <Button
              type="submit"
              color="primary"
              size="lg"
              className="w-full font-semibold"
              isLoading={isSubmitting}
            >
              이메일로 로그인
            </Button>
          </Form>

          <div className="flex items-center gap-3 my-6">
            <Divider className="flex-1" />
            <span className="text-xs text-foreground-500 px-2">또는</span>
            <Divider className="flex-1" />
          </div>

          <div className="space-y-3">
            <Button
              variant="bordered"
              size="lg"
              className="w-full bg-warning-50 border-warning-200 hover:bg-warning-100 text-warning-900 font-semibold"
              onPress={handleKakaoLogin}
              startContent={
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 3C7.03 3 3 6.47 3 10.75c0 2.74 1.81 5.14 4.5 6.46l-1.19 4.38c-.1.37.33.66.64.43l5.18-3.7c.24.02.48.03.72.03 4.97 0 9-3.47 9-7.75S16.97 3 12 3z" />
                </svg>
              }
            >
              카카오톡으로 로그인
            </Button>

            <Button
              variant="bordered"
              size="lg"
              className="w-full border-divider hover:bg-content2 font-semibold"
              onPress={handleClickGoogle}
              startContent={
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              }
            >
              구글로 로그인
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
